import { useListSpaces } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import type { ListSpacesParams } from '@cocoonspace/sdk-js/types/listSpacesParams'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { Venue } from '@cocoonspace/sdk-js/types/venue'
import { getCleanObject } from '@cocoonspace/shared/utils/get-clean-object'
import { Search } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import {
	type inferParserType,
	parseAsArrayOf,
	parseAsString,
	useQueryStates,
} from 'nuqs'
import { useMemo } from 'react'
import { Skeleton } from '~/components/ui/skeleton'
import { SearchCapacityFilter } from '~/domains/search/components/filters/search-capacity-filter'
import { SearchFeaturesFilter } from '~/domains/search/components/filters/search-features-filter'
import { TimeslotFilter } from '~/domains/search/components/filters/timeslot-filter'
import SpaceCard from '~/domains/search/components/space-card'

type Filters = inferParserType<typeof parsers>

const parsers = {
	capacity: parseAsString,
	features: parseAsArrayOf(parseAsString),
	day: parseAsArrayOf(parseAsString),
	start: parseAsString,
	end: parseAsString,
}

const SpaceListWrapper = ({
	venueId,
	filters,
}: {
	venueId: Venue['id']
	filters: Filters
}) => {
	const { t } = useTranslation()

	const { data: spacesByFloorGroups, isLoading } = useListSpaces(
		{
			venues: [venueId],
			...(getCleanObject(filters) as ListSpacesParams),
		},
		{
			query: {
				select: ({ data }) => {
					const groups = data.reduce(
						(acc, space) => {
							const floor = space.address?.floor ?? -1

							acc[floor] = acc[floor] || []
							acc[floor].push(space)

							return acc
						},
						{} as Record<number, Space[]>,
					)

					return Object.entries(groups).map(([floor, spaces]) => ({
						floor: Number(floor) > 0 ? Number(floor) : undefined,
						spaces,
					}))
				},
			},
		},
	)

	const hasMultipleFloors = useMemo(() => {
		const floors = spacesByFloorGroups?.filter(({ floor }) => !!floor)
		return floors?.length && floors.length > 1
	}, [spacesByFloorGroups])

	if (isLoading) {
		return (
			<div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton
						key={i}
						className='aspect-[366/175]'
					/>
				))}
			</div>
		)
	}

	if (!spacesByFloorGroups?.length) {
		return (
			<div className='flex flex-col items-center justify-center gap-2 py-24 text-center text-slate-400'>
				<Search className='size-10' />
				{t('search:results.noResults.noCocoonMatchSearch.title')}
			</div>
		)
	}

	return (
		<div className='flex flex-col gap-y-6'>
			{spacesByFloorGroups.map(({ floor, spaces }) => (
				<div
					key={`${spaces[0].venue_slug}-${floor}`}
					className='flex flex-col gap-y-2'
				>
					{hasMultipleFloors && !!floor && (
						<h2 className='font-semibold text-lg leading-none'>
							{t('space:floor', { count: floor })}
						</h2>
					)}

					<div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
						{spaces.map((space) => (
							<SpaceCard
								key={space.id}
								space={space}
								href={{
									pathname: `/${space.zone}/${space.slug}`,
									query: getCleanObject({
										day: filters.day,
										start: filters.start,
										end: filters.end,
									}),
								}}
							/>
						))}
					</div>
				</div>
			))}
		</div>
	)
}

export const VenueSpaces = ({ venue }: { venue: Venue }) => {
	const { t } = useTranslation()

	const [filters, setFilters] = useQueryStates(parsers, {
		history: 'push',
		shallow: true,
	})

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex flex-col items-start justify-between gap-2 lg:flex-row lg:items-center lg:gap-4'>
				<h2 className='font-semibold text-2xl'>
					{t('venue:location.spaces.title')}
				</h2>

				<div className='flex flex-wrap gap-2 lg:gap-4'>
					<SearchCapacityFilter
						className='w-[135px]'
						value={filters.capacity || ''}
						onChange={(capacity) => {
							setFilters({
								capacity: capacity || null,
							})
						}}
					/>

					<TimeslotFilter
						className='max-md:flex-[3] md:w-[210px]'
						value={{
							day: filters.day,
							start: filters.start,
							end: filters.end,
						}}
						onChange={(timeSlot) => {
							setFilters({
								day: timeSlot?.day || null,
								start: timeSlot?.start || null,
								end: timeSlot?.end || null,
							})
						}}
					/>

					<SearchFeaturesFilter
						value={filters.features as string[] | undefined}
						onChange={(features) => {
							setFilters({
								features: features || null,
							})
						}}
					/>
				</div>
			</div>

			<SpaceListWrapper
				venueId={venue.id}
				filters={filters}
			/>
		</div>
	)
}
